import { Connection, PublicKey } from '@solana/web3.js';

/** UTF-8 bytes for Anchor `b"split"` — avoid Node `Buffer` (not in browser without a polyfill). */
const SPLIT_SEED = new TextEncoder().encode('split');
const MAX_SOLANA_PAYEE_COUNTS = 20;

export function clusterFromSolanaRpcUrl(rpcUrl: string): 'devnet' | 'mainnet' {
  const u = rpcUrl.toLowerCase();
  if (u.includes('mainnet') && !u.includes('devnet')) return 'mainnet';
  return 'devnet';
}

export function clusterFromSuiRpcUrl(rpcUrl: string): 'devnet' | 'mainnet' {
  const u = rpcUrl.toLowerCase();
  if (u.includes('mainnet')) return 'mainnet';
  return 'devnet';
}

export function normalizeSuiAddress(a: string): string {
  const s = a.trim().toLowerCase();
  return s.startsWith('0x') ? s : `0x${s}`;
}

/** Anchor seeds: [b"split", creator, num_payees as u8]. */
export async function discoverSolanaSplitPdas(
  creatorBase58: string,
  programIdBase58: string,
  rpcUrl: string
): Promise<string[]> {
  let creator: PublicKey;
  let programId: PublicKey;
  try {
    creator = new PublicKey(creatorBase58);
    programId = new PublicKey(programIdBase58);
  } catch {
    return [];
  }

  const connection = new Connection(rpcUrl, 'confirmed');
  const pdas: PublicKey[] = [];
  for (let n = 1; n <= MAX_SOLANA_PAYEE_COUNTS; n++) {
    const [pda] = PublicKey.findProgramAddressSync(
      [SPLIT_SEED, creator.toBytes(), new Uint8Array([n])],
      programId
    );
    pdas.push(pda);
  }

  const infos = await connection.getMultipleAccountsInfo(pdas);
  const out: string[] = [];
  for (let i = 0; i < infos.length; i++) {
    const info = infos[i];
    if (!info) continue;
    if (!info.owner.equals(programId)) continue;
    out.push(pdas[i].toBase58());
  }
  return out;
}

/**
 * Discover split PDAs where `payeeBase58` is included in the payees array.
 * This scans all program accounts; best for low/moderate account counts.
 */
export async function discoverSolanaSplitsForPayee(
  payeeBase58: string,
  programIdBase58: string,
  rpcUrl: string
): Promise<string[]> {
  let payee: PublicKey;
  let programId: PublicKey;
  try {
    payee = new PublicKey(payeeBase58);
    programId = new PublicKey(programIdBase58);
  } catch {
    return [];
  }

  const connection = new Connection(rpcUrl, 'confirmed');
  const accounts = await connection.getProgramAccounts(programId);
  const target = payee.toBytes();
  const out: string[] = [];

  // Layout (after 8-byte discriminator):
  // creator(32) authority(32) protocol_fee_recipient(32) fee_bps(2) num_payees(1) bump(1) total_released(8)
  // payees([20]pubkey) ...
  const NUM_PAYEES_OFFSET = 8 + 32 + 32 + 32 + 2;
  const PAYEES_OFFSET = 8 + 32 + 32 + 32 + 2 + 1 + 1 + 8;
  const MAX_PAYEES = 20;
  const PUBKEY_LEN = 32;

  for (const a of accounts) {
    const data = a.account.data;
    if (!data || data.length < PAYEES_OFFSET + PUBKEY_LEN) continue;
    const numPayees = Math.min(data[NUM_PAYEES_OFFSET] ?? 0, MAX_PAYEES);
    let match = false;
    for (let i = 0; i < numPayees; i++) {
      const off = PAYEES_OFFSET + i * PUBKEY_LEN;
      const slice = data.subarray(off, off + PUBKEY_LEN);
      let eq = true;
      for (let j = 0; j < PUBKEY_LEN; j++) {
        if (slice[j] !== target[j]) {
          eq = false;
          break;
        }
      }
      if (eq) {
        match = true;
        break;
      }
    }
    if (match) out.push(a.pubkey.toBase58());
  }
  return out;
}

async function suiRpc<T>(rpcUrl: string, method: string, params: unknown[]): Promise<T> {
  const res = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  });
  const json = (await res.json()) as { result?: T; error?: { message: string } };
  if (json.error) throw new Error(json.error.message);
  return json.result as T;
}

/** Resolve Split object id from a publish transaction digest. */
export async function resolveSuiSplitObjectIdFromDeployDigest(
  deployDigest: string,
  rpcUrl: string
): Promise<string | null> {
  try {
    const tx = await suiRpc<{
      objectChanges?: Array<{ type: string; objectType?: string; objectId?: string }>;
    }>(rpcUrl, 'sui_getTransactionBlock', [deployDigest, { showObjectChanges: true }]);
    const changes = tx?.objectChanges ?? [];
    const created = changes.find(
      (c) => c.type === 'created' && String(c.objectType || '').includes('::splitter::Split')
    );
    return created?.objectId ?? null;
  } catch {
    return null;
  }
}

type SuiEventPage = {
  data?: Array<{ parsedJson?: Record<string, unknown> }>;
  nextCursor?: string | null;
  hasNextPage?: boolean;
};

/**
 * Paginate SplitCreated events and return split object ids where creator matches (shared objects are not “owned” by address).
 */
export async function discoverSuiSplitObjectIdsForCreator(
  creatorAddress: string,
  packageIdRaw: string,
  rpcUrl: string,
  maxPages = 8
): Promise<string[]> {
  const creator = normalizeSuiAddress(creatorAddress);
  const pkg = normalizeSuiAddress(packageIdRaw);
  const eventType = `${pkg}::splitter::SplitCreated`;
  const seen = new Set<string>();
  let cursor: string | null | undefined = null;
  let pages = 0;

  while (pages < maxPages) {
    const page: SuiEventPage = await suiRpc<SuiEventPage>(rpcUrl, 'suix_queryEvents', [
      {
        query: { MoveEventType: eventType },
        cursor: cursor ?? null,
        limit: 50,
        descendingOrder: true,
      },
    ]);
    const data = page?.data ?? [];
    for (const ev of data) {
      const j = ev.parsedJson;
      if (!j) continue;
      const c = j.creator;
      if (typeof c !== 'string' || normalizeSuiAddress(c) !== creator) continue;
      const sid = j.split_id;
      if (typeof sid === 'string' && sid.length > 0) {
        seen.add(sid);
      }
    }
    pages += 1;
    if (!page?.hasNextPage || !page.nextCursor) break;
    cursor = page.nextCursor;
  }

  return Array.from(seen);
}
